import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SideBar from '../Components/SideBar';
import Header from '../Components/Header';
import { Button, Paper, Typography, Box, Divider } from '@mui/material';
import dayjs from 'dayjs';

// Add print styles
const printStyles = `
    @media print {
        body * {
            visibility: hidden;
        }
        .printable-content, .printable-content * {
            visibility: visible;
        }
        .printable-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            color: black !important;
        }
        .no-print {
            display: none !important;
        }
        .print-only {
            display: block !important;
        }
        .printable-content .MuiPaper-root {
            box-shadow: none !important;
            border: 1px solid #ccc !important;
        }
        .printable-content .MuiTypography-root {
            color: black !important;
        }
        @page {
            margin: 0.5in;
            size: A4;
        }
    }
`;

const RenewInvoice = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [invoiceData, setInvoiceData] = useState(null);

    useEffect(() => {
        if (location.state?.invoiceData) {
            setInvoiceData(location.state.invoiceData);
        } else {
            navigate('/all-deceased');
        }
    }, [location.state, navigate]);

    const printInvoice = () => {
        // Add a small delay to ensure styles are applied
        setTimeout(() => {
            window.print();
        }, 100);
    };

    const handleBack = () => {
        navigate('/all-deceased');
    };

    if (!invoiceData) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <Typography>Loading invoice data...</Typography>
            </div>
        );
    }

    const { deceasedData, feeData, maintenanceFeeAmount, totalAmount, tblid } = invoiceData;

    // Calculate maintenance fee percentage
    const maintenanceFeePercentage = feeData && feeData > 0 
        ? ((maintenanceFeeAmount / feeData) * 100).toFixed(2)
        : 0;

    return (
        <>
            <style>{printStyles}</style>
            <div className='grid grid-cols-12'>
                <div className='col-span-2 hidden lg:block no-print'>
                    <SideBar />
                </div>
                <div className='col-span-12 lg:col-span-10 bg-[#D2D2CF]'>
                    <div className="no-print">
                        <Header />
                        <div className="flex items-center justify-between py-6">
                            <h1 className="text-4xl font-bold text-[#111927] py-2">
                                Renewal Invoice
                            </h1>
                            <div className="flex space-x-2">
                                <Button 
                                    onClick={handleBack}
                                    variant="outlined" 
                                    style={{ borderColor: '#0F1D48', color: '#0F1D48' }}
                                >
                                    Back to All Deceased
                                </Button>
                                <Button 
                                    onClick={printInvoice}
                                    variant="contained" 
                                    style={{ backgroundColor: '#0F1D48', color: 'white' }}
                                >
                                    Print Invoice
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="printable-content">
                        <Paper elevation={3} className="bg-white mt-8 p-8 mb-8" style={{ borderRadius: 20 }}>
                            {/* Invoice Header */}
                            <div className="text-center mb-8">
                                <Typography variant="h3" className="font-bold text-[#0F1D48] mb-2">
                                    GRACELAND MEMORIAL PARK
                                </Typography>
                                <Typography variant="h5" className="text-gray-600 mb-4">
                                    Deceased Renewal Invoice
                                </Typography>
                                <Typography variant="body1" className="text-gray-500">
                                    Invoice Date: {dayjs().format('MMMM DD, YYYY')}
                                </Typography>
                                <Typography variant="body1" className="text-gray-500">
                                    Invoice #: REN-{tblid}-{dayjs().format('YYYYMMDD')}
                                </Typography>
                            </div>

                            <Divider className="my-6" />

                            {/* Deceased Information */}
                            <div className="mb-8">
                                <Typography variant="h5" className="font-bold text-[#0F1D48] mb-4">
                                    Deceased Information
                                </Typography>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Full Name
                                        </Typography>
                                        <Typography variant="body1" className="font-semibold">
                                            {deceasedData.fname} {deceasedData.mname} {deceasedData.lname}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Gender
                                        </Typography>
                                        <Typography variant="body1" className="font-semibold">
                                            {deceasedData.gender}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Date of Death
                                        </Typography>
                                        <Typography variant="body1" className="font-semibold">
                                            {deceasedData.dod}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Bed Location
                                        </Typography>
                                        <Typography variant="body1" className="font-semibold">
                                            {deceasedData.bed}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Age
                                        </Typography>
                                        <Typography variant="body1" className="font-semibold">
                                            {deceasedData.Aged}
                                        </Typography>
                                    </div>
                                    <div>
                                        <Typography variant="subtitle2" color="textSecondary">
                                            Sale of Grave
                                        </Typography>
                                        <Typography variant="body1" className="font-semibold">
                                            {deceasedData.sale_of_grave}
                                        </Typography>
                                    </div>
                                </div>
                            </div>

                            <Divider className="my-6" />

                            {/* Fee Breakdown */}
                            <div className="mb-8">
                                <Typography variant="h5" className="font-bold text-[#0F1D48] mb-4">
                                    Fee Breakdown
                                </Typography>
                                
                                <div className="bg-gray-50 p-6 rounded-lg">
                                    <div className="space-y-4">
                                        <div className="flex justify-between items-center py-2">
                                            <Typography variant="body1" className="font-medium">
                                                Base Fee Amount
                                            </Typography>
                                            <Typography variant="h6" className="font-bold text-green-600">
                                                GH₵ {Number(feeData).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </Typography>
                                        </div>
                                        
                                        <div className="flex justify-between items-center py-2">
                                            <Typography variant="body1" className="font-medium">
                                                Maintenance Fee
                                            </Typography>
                                            <Typography variant="h6" className="font-bold text-blue-600">
                                                GH₵ {Number(maintenanceFeeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({maintenanceFeePercentage}%)
                                            </Typography>
                                        </div>
                                        
                                        <Divider />
                                        
                                        <div className="flex justify-between items-center py-3 bg-green-100 rounded-lg px-4">
                                            <Typography variant="h5" className="font-bold text-gray-800">
                                                Total Amount
                                            </Typography>
                                            <Typography variant="h4" className="font-bold text-green-700">
                                            GH₵ {Number(maintenanceFeeAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                {/* GH₵ {Number(totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} */}
                                            </Typography>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <Divider className="my-6" />

                            {/* Footer */}
                            <div className="text-center text-gray-500">
                                <Typography variant="body2">
                                    Thank you for choosing Graceland Memorial Park
                                </Typography>
                                <Typography variant="body2" className="mt-2">
                                    For inquiries, contact us at: info@gracelandmemorial.com
                                </Typography>
                            </div>
                        </Paper>
                    </div>
                </div>
            </div>
        </>
    );
};

export default RenewInvoice;